import { useNavigate } from "react-router-dom";
import { ShoppingCart, X, Trash, Plus, Minus } from "@phosphor-icons/react";
import { useCart, formatMoney } from "@/lib/cart";
import { useT } from "@/lib/i18n";
import { useConfirm } from "@/hooks/use-confirm";

export default function CartDrawer() {
  const { items, subtotal, currency, drawerOpen, setDrawerOpen, updateQty, removeItem, clear } = useCart();
  const navigate = useNavigate();
  const t = useT();
  const confirm = useConfirm();

  if (!drawerOpen) return null;

  const handleCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <div data-testid="cart-drawer" className="fixed inset-0 z-[60] flex justify-end">
      {/* Backdrop */}
      <button
        aria-label="Close cart"
        onClick={() => setDrawerOpen(false)}
        className="absolute inset-0 bg-slate-900/30"
        data-testid="cart-drawer-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-md bg-white h-full shadow-2xl border-l border-slate-200 flex flex-col"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 40px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShoppingCart size={20} weight="bold" className="text-blue-700" />
            <h2 className="text-base font-black tracking-tight font-['Chivo']">{t("cart.title", "Your Cart")}</h2>
            <span data-testid="cart-drawer-count" className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">
              {items.length} {items.length === 1 ? "item" : "items"}
            </span>
          </div>
          <button
            data-testid="cart-drawer-close"
            onClick={() => setDrawerOpen(false)}
            className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-700"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {items.length === 0 ? (
            <div data-testid="cart-empty-state" className="flex flex-col items-center justify-center h-full text-center px-6">
              <ShoppingCart size={40} weight="thin" className="text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-600">{t("cart.empty", "Your cart is empty")}</p>
              <p className="text-xs text-slate-400 mt-1">{t("cart.empty_hint", "Browse the price list and add items with the + Cart button.")}</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const key = `${it.product_code}|${it.currency}|${it.discount || 0}|${it.kit_label || ""}`;
                const qty = Number(it.quantity) || 1;
                const disc = Math.max(0, Math.min(1, Number(it.discount) || 0));
                const gross = qty * (Number(it.price) || 0);
                const line = gross * (1 - disc);
                const kitLabelMap = { break_in: "Break-In Kit", service_3000: "3,000-hr Kit" };
                return (
                  <li key={key} data-testid={`cart-item-${it.product_code}`} className="py-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{it.product_name}</p>
                      <p className="text-[11px] font-mono text-slate-400 truncate">{it.product_code} · {it.currency}</p>
                      {disc > 0 && (
                        <p className="text-[10px] font-bold mt-0.5">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">
                            {kitLabelMap[it.kit_label] || "Kit"} · −{Math.round(disc * 100)}%
                          </span>
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <div className="inline-flex items-center border border-slate-200 rounded-sm">
                          <button
                            data-testid={`cart-qty-dec-${it.product_code}`}
                            onClick={() => updateQty(it.product_code, it.currency, qty - 1)}
                            disabled={qty <= 1}
                            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Decrease qty"
                          >
                            <Minus size={12} />
                          </button>
                          <input
                            data-testid={`cart-qty-input-${it.product_code}`}
                            type="number"
                            min="1"
                            value={qty}
                            onChange={(e) => updateQty(it.product_code, it.currency, e.target.value)}
                            className="w-10 h-7 text-xs text-center border-x border-slate-200 focus:outline-none focus:bg-blue-50"
                          />
                          <button
                            data-testid={`cart-qty-inc-${it.product_code}`}
                            onClick={() => updateQty(it.product_code, it.currency, qty + 1)}
                            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50"
                            title="Increase qty"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <button
                          data-testid={`cart-remove-${it.product_code}`}
                          onClick={() => removeItem(it.product_code, it.currency)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          title="Remove"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-sm font-bold text-slate-900 font-mono">{formatMoney(line, it.currency)}</p>
                      {disc > 0 ? (
                        <p className="text-[11px] text-slate-400 font-mono">
                          <span className="line-through">{formatMoney(gross, it.currency)}</span>
                          <span className="text-emerald-600 ml-1">−{Math.round(disc * 100)}%</span>
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400 font-mono">{formatMoney(it.price, it.currency)} ea</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-4 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("cart.subtotal", "Subtotal")}</span>
              <span data-testid="cart-subtotal" className="text-lg font-black font-mono text-slate-900">
                {formatMoney(subtotal, currency)}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">
              {t("cart.shipping_note", "Shipping, taxes & any start-up charges are finalized during checkout.")}
            </p>
            <div className="flex gap-2">
              <button
                data-testid="cart-clear-button"
                onClick={async () => {
                  if (await confirm({
                    title: t("cart.empty_confirm", "Empty the cart?"),
                    confirmLabel: t("cart.empty_button", "Empty"),
                    cancelLabel: t("action.cancel", "Cancel"),
                    tone: "danger",
                  })) clear();
                }}
                className="h-10 px-3 text-xs font-semibold text-slate-500 border border-slate-300 rounded-sm hover:bg-white"
              >
                {t("cart.empty_button", "Empty")}
              </button>
              <button
                data-testid="cart-checkout-button"
                onClick={handleCheckout}
                className="flex-1 h-10 text-sm font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 transition-colors"
              >
                {t("cart.checkout", "Checkout")} →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function CartHeaderButton() {
  const { count, setDrawerOpen } = useCart();
  const t = useT();
  return (
    <button
      data-testid="cart-header-button"
      onClick={() => setDrawerOpen(true)}
      title={t("cart.title", "Open cart")}
      className="relative w-8 h-8 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-blue-700 transition-colors"
    >
      <ShoppingCart size={16} weight="bold" />
      {count > 0 && (
        <span
          data-testid="cart-header-count"
          className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-700 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
